import React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import type { Money } from "@workspace/accounting/types";
import { isHttpStatus } from "@workspace/base-api/fetchJson";
import { stripeApi, type ReaderCancelResult } from "@workspace/stripe";

import {
	completionRetryDelay,
	shouldRetryCompletion,
} from "./card-payment-recovery";

/**
 * HA-767 — why a Cancel did not happen. "processing": the reader is taking the card. "reader": the
 * reader did not confirm cancelling its collection. "taken": the server says the payment has been
 * taken or is being taken. "server": the cancel could not be made.
 */
export type CardCancelResult =
	| { cancelled: true }
	| {
			cancelled: false;
			reason: "processing" | "reader" | "taken" | "server";
			message?: string;
	  };

export function useCardPayments({
	tenant,
	venueId,
}: {
	tenant: string;
	venueId: string;
}) {
	const [started, setStarted] = React.useState<boolean>(false);
	const [paymentId, setPaymentId] = React.useState<string | undefined>();
	const [amount, setAmount] = React.useState<Money | undefined>();
	const [error, setError] = React.useState<
		{ type: string; message?: string } | undefined
	>();

	// HA-767 — what TerminalPayment reports about the reader. Refs, so a Cancel pressed mid-render
	// reads where the reader is now rather than where it was when the handler was created.
	const processing = React.useRef(false);
	const readerCancel = React.useRef<(() => Promise<ReaderCancelResult>) | null>(
		null,
	);

	const startPayment = useMutation({
		...stripeApi.startCardPaymentMutation(tenant, venueId),
		onError: () => {
			setError({
				type: "start",
				message: "There was an error while trying to start the card payment.",
			});
		},
		// HA-768 / D-HA-24 24-2 — one attempt, so a failure reaches the operator (with a Retry, from
		// startCardPayment) inside fetchJson's 10-second timeout. This retried up to four times
		// with backoff, and read `e.response` off errors that have none (a timeout, a dropped
		// connection), so the failure the operator most needed to see threw inside the retryer.
		retry: false,
		onSettled(_data, _error, _variables, _onMutateResult, context) {
			context.client.invalidateQueries({
				queryKey: [tenant, venueId, "epos"],
			});
		},
	});

	const completePayment = useMutation({
		...stripeApi.completeCardPaymentMutation(tenant, venueId, paymentId || ""),
		onError: () => {
			setError({
				type: "complete",
				message:
					"There was an error while trying to complete the card payment.",
			});
		},
		// HA-886 — the completion is idempotent on the server, so what says nothing about the payment (a
		// timeout, a dropped connection, a 5xx) is asked again with backoff. The old rule read
		// `e.response` off errors that have none and gave up on any 500.
		retry: shouldRetryCompletion,
		retryDelay: completionRetryDelay,
		onSettled(_data, _error, _variables, _onMutateResult, context) {
			context.client.invalidateQueries({
				queryKey: [tenant, venueId, "epos"],
			});
		},
	});

	const getPaymentStatus = useQuery({
		...stripeApi.getPaymentStatusOptions(tenant, venueId, paymentId || ""),
		enabled: !!paymentId && started && !startPayment.isPending,
		refetchInterval: (data) => {
			if (data.state.data?.status === "Pending") {
				return 2000; // Refetch every 2 seconds if payment is still pending
			}

			return false; // Stop refetching if payment is completed or failed
		},
	});

	const clear = () => {
		setStarted(false);
		setPaymentId(undefined);
		setError(undefined);
		setAmount(undefined);
		startPayment.reset();
		completePayment.reset();
	};

	return {
		paymentId,
		amount,

		/** HA-767 — wired to TerminalPayment's onProcessingChange. */
		setProcessing: (value: boolean) => {
			processing.current = value;
		},
		/** HA-767 — wired to TerminalPayment's onCancelReady. */
		setReaderCancel: (cancel: (() => Promise<ReaderCancelResult>) | null) => {
			readerCancel.current = cancel;
		},

		/**
		 * HA-767 — HA-139 Notes and AC3: cancel the card transaction, through the reader and the
		 * Payment Intent, so another method can be taken. Only what really is cancelled counts:
		 * - while the reader is processing the card the SDK cannot stop it, so this refuses;
		 * - an in-progress collection is cancelled on the reader first, and a reader that does not
		 *   confirm it refuses too, because it may still take a tap;
		 * - then the server cancels the Payment Intent, and a 409 (taken, or being taken) refuses.
		 * Local state is cleared only once all of that has said yes. A refusal changes nothing.
		 */
		cancel: async (): Promise<CardCancelResult> => {
			// Read through a function: the reader can start processing during the await below, and a
			// plain `processing.current` check would stay narrowed across it.
			const isProcessing = () => processing.current;

			if (isProcessing()) return { cancelled: false, reason: "processing" };

			if (readerCancel.current && (await readerCancel.current()) === "failed") {
				return { cancelled: false, reason: "reader" };
			}

			if (isProcessing()) return { cancelled: false, reason: "processing" };

			if (paymentId) {
				try {
					await stripeApi.cancelCardPayment(tenant, paymentId);
				} catch (e) {
					return isHttpStatus(e, 409)
						? { cancelled: false, reason: "taken", message: e.problem?.detail }
						: {
								cancelled: false,
								reason: "server",
								message: e instanceof Error ? e.message : undefined,
							};
				}
			}

			clear();
			return { cancelled: true };
		},
		reset: clear,
		status: !paymentId
			? "None"
			: !error
				? getPaymentStatus.data?.status || "None"
				: "Failed",
		clientSecret: getPaymentStatus.data?.clientSecret || undefined,
		// HA-858 (S2): the server's call on the reader's tip screen, kept on the payment's saga.
		skipTipping: getPaymentStatus.data?.skipTipping,
		errorMessage: error?.message || getPaymentStatus.data?.errorMessage,
		query: getPaymentStatus,

		setError: (e: Error) => {
			setError({
				type: "terminal",
				message: e.message,
			});
			console.error(e);
		},

		/** HA-554 Part 2 — for an order (optionally a split of it) or for a tab (Pay Tab): exactly one of the two. */
		start: (
			payment: { id: string; amount: Money; tip?: Money } & (
				{ orderId: string; tabId?: never } | { tabId: string; orderId?: never }
			),
		) => {
			setStarted(true);
			setPaymentId(payment.id);
			setAmount(payment.amount);

			return startPayment.mutateAsync({
				...payment,
				paymentId: payment.id,
			});
		},
		success(transactionId: string) {
			return completePayment.mutateAsync({
				paymentId: paymentId || "",
				externalId: transactionId,
			});
		},
	};
}
